import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { GenericTabBar } from './GenericTabBar';
import { Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { colors } from '@/design-system/colors';
import { AddCommentButton } from './StandardButtons';
import { formatUserDisplayName } from '@/lib/user-display';
import "@/design-system/typography.css";
import '@/design-system/forms.css';

// Comment audience -- distinguishes who a comment is for. Optional: the 9
// entity types that don't need audience separation (internal-only work
// items) simply never pass `audiences`, and every comment they read/write
// stays at the DB default ('internal') with no picker/badge shown.
// Proposals is the first caller that passes `audiences` (Internal/Client/
// Vendor) -- see ProposalsForm.tsx and phase88_proposal_comments.sql.
export interface CommentAudienceOption {
  value: string;
  label: string;
}

/**
 * The single, reusable Team/Client/Vendor audience set -- pass this as
 * `audiences` on any GenericCommentsSection that needs the filter, instead
 * of each modal defining its own identical array (Proposals/To-Do's/
 * Checklists used to each have their own copy of the exact same 3
 * options). Only meaningful for entity types whose backend RLS actually
 * understands 'client'/'vendor' for that entity_type (see
 * phase88_proposal_comments.sql's can_view_comment_audience()) --
 * passing it for an entity type without a matching case there still works
 * as a pure UI filter/categorization (company members already see every
 * audience regardless), it just doesn't grant any NEW external visibility
 * until the backend has a case for that entity_type too.
 */
export const TEAM_CLIENT_VENDOR_AUDIENCES: CommentAudienceOption[] = [
  { value: 'internal', label: 'Team' },
  { value: 'client', label: 'Client' },
  { value: 'vendor', label: 'Vendor' },
];

export interface GenericComment {
  id: string;
  content: string;
  created_at: string;
  created_by: string;
  audience?: string;
  author: {
    id: string;
    display_name?: string | null;
  };
}

interface GenericCommentsSectionProps {
  comments: GenericComment[];
  onAddComment: (content: string, audience?: string) => Promise<void> | void;
  onDeleteComment?: (commentId: string) => Promise<void> | void;
  mode: 'create' | 'edit';
  disabled?: boolean;
  placeholder?: string;
  /** When provided, shows an audience picker before posting and an audience badge per comment. Omit for entities that don't need audience separation. */
  audiences?: CommentAudienceOption[];
}

export function GenericCommentsSection({
  comments = [],
  onAddComment,
  onDeleteComment,
  mode,
  disabled = false,
  placeholder,
  audiences,
}: GenericCommentsSectionProps) {
  const [newComment, setNewComment] = useState('');
  const [selectedAudience, setSelectedAudience] = useState<string>(audiences?.[0]?.value ?? 'internal');

  // When `audiences` is provided, the tabs both pick which audience a new
  // comment posts as AND filter which comments are shown -- server/RLS
  // still independently enforces who can read which audience (see
  // phase88_proposal_comments.sql's audience-aware policies); this filter
  // is a display convenience on top of that, not the security boundary.
  const visibleComments = useMemo(
    () => (audiences ? comments.filter((c) => (c.audience ?? 'internal') === selectedAudience) : comments),
    [comments, audiences, selectedAudience]
  );

  const groupComments = (comments: GenericComment[]) => {
    const grouped: GenericComment[][] = [];
    let currentGroup: GenericComment[] = [];

    comments.forEach((comment, index) => {
      if (index === 0) {
        currentGroup.push(comment);
        return;
      }

      const prevComment = comments[index - 1];
      const timeDiff = Math.abs(
        new Date(comment.created_at).getTime() - new Date(prevComment.created_at).getTime()
      );
      const sameAuthor = comment.created_by === prevComment.created_by;
      const sameAudience = (comment.audience ?? 'internal') === (prevComment.audience ?? 'internal');

      if (sameAuthor && sameAudience && timeDiff <= 60000) {
        currentGroup.push(comment);
      } else {
        if (currentGroup.length > 0) {
          grouped.push([...currentGroup]);
        }
        currentGroup = [comment];
      }
    });

    if (currentGroup.length > 0) {
      grouped.push(currentGroup);
    }

    return grouped;
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      await onAddComment(newComment.trim(), audiences ? selectedAudience : undefined);
      setNewComment('');
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }
    if (onDeleteComment) {
      await onDeleteComment(commentId);
    }
  };

  const defaultPlaceholder = mode === 'create' 
    ? "Comments are available after creating the item" 
    : "Add a comment...";

  return (
    <div className="grid gap-2">
      <Label className="font-bold">Comments</Label>
      {audiences && (
        <GenericTabBar
          tabs={audiences}
          activeTab={selectedAudience}
          onTabChange={setSelectedAudience}
        />
      )}
      {visibleComments.length > 0 && (
        <div className="border-2 border-border rounded-md divide-y divide-border">
          {groupComments(visibleComments).map((group, groupIndex) => (
            <div key={groupIndex} className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="font-form-label">
                  {formatUserDisplayName(group[0].author)}
                </p>
                <p className="text-body-small-muted">
                  {group[0].created_at ? format(new Date(group[0].created_at), 'MMM d, yyyy h:mm a') : ''}
                </p>
              </div>
              <div className="space-y-2">
                {group.map((comment) => (
                  <div key={comment.id} id={`comment-${comment.id}`} className="flex items-start gap-3">
                    <div className="flex-1">
                      <p className="text-body">{comment.content}</p>
                    </div>
                    {onDeleteComment && mode === 'edit' && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteComment(comment.id)}
                        className="h-8 w-8 p-0"
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.buttonTertiaryHover; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Textarea
          compact
          rows={2}
          placeholder={placeholder || defaultPlaceholder}
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          className="flex-1"
          disabled={disabled || mode === 'create'}
        />
        <AddCommentButton
          onClick={handleAddComment}
          disabled={!newComment.trim() || disabled || mode === 'create'}
        />
      </div>
    </div>
  );
}
